import { jsPDF } from 'jspdf';
import { M37_CO_GROUPS, VEHICLE_DOTACION_CATEGORIES } from '../../services/api';
import { normalizeM37CoGroup } from '../../utils/m37CoRules';

const PAGE_W = 215.9;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;
const LINE_H = 5.5;

function warehouseLabel(id) {
  const w = String(id || '').trim();
  if (w === 'popayan') return 'Popayán';
  if (w === 'santander') return 'Santander de Quilichao';
  return w || '—';
}

function coGroupLabel(key) {
  const k = normalizeM37CoGroup(key);
  const found = M37_CO_GROUPS.find((g) => g.key === k);
  return found?.label || (k ? k : '—');
}

function dotacionLabel(key) {
  const k = String(key || '').trim();
  const found = VEHICLE_DOTACION_CATEGORIES.find((c) => c.key === k);
  return found?.label || (k ? k : '—');
}

function assigneeTypeLabel(type) {
  return String(type || '').toLowerCase() === 'vehicle' ? 'Vehículo (dotación carro)' : 'Persona';
}

function pdfText(doc, text, x, y, maxW) {
  const lines = doc.splitTextToSize(String(text ?? ''), maxW);
  doc.text(lines, x, y);
  return y + lines.length * LINE_H;
}

function ensureSpace(doc, y, need = 12) {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + need > pageH - MARGIN) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

function normalizeItems(data) {
  const raw = data?.items;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((it) => ({
      materialName: it.materialName || it.materialId || '',
      materialCode: it.materialCode || '',
      quantity: it.quantity ?? '',
      responsibleName: it.responsibleName || data.responsibleName || '',
      responsibleDoc: it.responsibleDoc || data.responsibleDoc || '',
      serial: it.serial || '',
      brandName: it.brandName || '',
      evidenceCount: Number(it.evidenceCount ?? (Array.isArray(it.evidencePhotos) ? it.evidencePhotos.length : 0)) || 0,
    }));
  }
  const name = data.materialName || data.materialId || '';
  const code = String(data.materialCode || '').trim();
  return [{
    materialName: code ? `${code} - ${name}` : name,
    materialCode: code,
    quantity: data.quantity ?? '',
    responsibleName: data.responsibleName || '',
    responsibleDoc: data.responsibleDoc || '',
    serial: data.serial || '',
    brandName: data.brandName || '',
    evidenceCount: Number(data.evidenceCount ?? (Array.isArray(data.evidencePhotos) ? data.evidencePhotos.length : 0)) || 0,
  }];
}

export function assignmentPdfPayloadFromRow(item, { type = 'Asignación', warehouses = [] } = {}) {
  const wh = (warehouses || []).find((w) => w.id === item.warehouseId);
  const rowItem = {
    materialName: item.materialName,
    materialCode: item.materialCode,
    quantity: item.quantity,
    responsibleName: item.responsibleName,
    responsibleDoc: item.responsibleDoc,
    serial: item.serial,
    brandName: item.brandName,
    evidenceCount: item.evidenceCount,
  };
  return {
    type,
    date: item.createdAt || item.assignDate || '',
    id: item.id,
    assignmentDbId: item.assignmentDbId || item.id,
    movementId: item.movementId,
    assigneeType: item.assigneeType,
    coGroup: item.coGroup,
    dotacionCategory: item.dotacionCategory,
    vehiclePlate: item.vehiclePlate,
    responsibleName: item.responsibleName,
    responsibleDoc: item.responsibleDoc,
    materialName: item.materialName,
    materialCode: item.materialCode,
    materialCategory: item.materialCategory,
    serial: item.serial,
    brandName: item.brandName,
    quantity: item.quantity,
    observations: item.notes,
    evidencePhotos: item.evidencePhotos,
    evidenceCount: item.evidenceCount,
    warehouseId: item.warehouseId,
    warehouseName: wh?.name,
    items: [rowItem],
  };
}

export function assignmentPdfPayloadFromReceipt(receipt) {
  if (!receipt) return null;
  const items = (Array.isArray(receipt.items) ? receipt.items : []).map((it) => ({
    ...it,
    responsibleName: it.responsibleName || receipt.responsibleName,
    responsibleDoc: it.responsibleDoc || receipt.responsibleDoc,
    evidenceCount: it.evidenceCount ?? (Array.isArray(it.evidencePhotos) ? it.evidencePhotos.length : 0),
  }));
  return {
    ...receipt,
    movementId: receipt.movementId || (receipt.entryRef ? String(receipt.entryRef).replace(/^MV-/i, '') : null),
    materialCategory: receipt.materialCategory || receipt.material_category,
    observations: receipt.observations || receipt.notes,
    items: items.length ? items : undefined,
  };
}

export function assignmentFormPdfFilename(data) {
  const type = String(data?.type || 'Asignación').toLowerCase();
  const prefix = type.includes('préstamo') || type.includes('prestamo') ? 'prestamo' : 'asignacion';
  const ref = data?.movementId || data?.entryRef || data?.id || data?.assignmentDbId;
  const safeRef = String(ref || '').replace(/[^\w-]+/g, '_').slice(0, 40);
  const stamp = new Date().toISOString().slice(0, 10);
  return `${prefix}-${safeRef || stamp}.pdf`;
}

/** Etiquetas del formulario web (AssignmentForm) — usadas en PDF y validación. */
export const ASSIGNMENT_FORM_FIELD_LABELS = [
  'Destinatario',
  'Proyecto (grupo CO)',
  'Persona responsable',
  'Vehículo',
  'Categoría dotación',
  'Bodega',
  'Categoría',
  'Material',
  'Marca (según material)',
  'Serial del equipo',
  'Cantidad',
  'Observaciones',
  'Evidencias',
];

export const ASSIGNMENT_FORM_TABLE_HEADERS = [
  'Material', 'Cant.', 'Responsable', 'Cédula', 'Serie', 'Marca', 'Evid.',
];

/**
 * Genera el documento jsPDF sin descargarlo (pruebas / validación).
 * @param {object} data
 * @returns {import('jspdf').jsPDF}
 */
export function buildAssignmentFormPdfDoc(data) {
  const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'portrait' });
  doc.setFont('helvetica', 'normal');
  let y = MARGIN;

  const docType = String(data?.type || 'Asignación').trim() || 'Asignación';
  const title = `Formulario de ${docType}`;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, MARGIN, y);
  y += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const metaParts = [];
  if (data?.date) metaParts.push(`Fecha: ${data.date}`);
  const mvRef = data?.entryRef || (data?.movementId ? `MV-${data.movementId}` : '');
  if (mvRef) metaParts.push(`Referencia: ${mvRef}`);
  if (metaParts.length) {
    y = pdfText(doc, metaParts.join('  |  '), MARGIN, y, CONTENT_W);
    y += 2;
  }

  const assigneeType = data?.assigneeType || (String(data?.responsibleDoc || '').startsWith('vehicle:') ? 'vehicle' : 'person');
  const items = normalizeItems(data);
  const first = items[0] || {};
  const materialDisplay = (() => {
    const name = data.materialName || first.materialName || '';
    const code = String(data.materialCode || first.materialCode || '').trim();
    if (code && name && !String(name).startsWith(code)) return `${code} - ${name}`;
    return name || code || '—';
  })();

  const fields = [
    ['Destinatario', assigneeTypeLabel(assigneeType)],
    ['Proyecto (grupo CO)', coGroupLabel(data?.coGroup || data?.co_group)],
    assigneeType === 'vehicle'
      ? ['Vehículo', data?.vehiclePlate || data?.responsibleName || '—']
      : ['Persona responsable', data?.responsibleName || first.responsibleName || '—'],
  ];
  if (assigneeType === 'vehicle') {
    fields.push(['Categoría dotación', dotacionLabel(data?.dotacionCategory || data?.dotacion_category)]);
  }
  fields.push(
    ['Bodega', data?.warehouseName || warehouseLabel(data?.warehouseId)],
    ['Categoría', data?.materialCategory || data?.category || first.material_category || '—'],
    ['Material', materialDisplay],
    ['Marca (según material)', data?.brandName || first.brandName || '—'],
    ['Serial del equipo', data?.serial || first.serial || '—'],
    ['Cantidad', String(data?.quantity ?? first.quantity ?? '—')],
    ['Observaciones', String(data?.observations || data?.notes || '').trim() || '—'],
    ['Evidencias', String(
      Number.isFinite(Number(data?.evidenceCount))
        ? Number(data.evidenceCount)
        : (Array.isArray(data?.evidencePhotos) ? data.evidencePhotos.length : (first.evidenceCount || 0))
    )],
  );

  doc.setFontSize(10);
  for (const [label, value] of fields) {
    y = ensureSpace(doc, y, LINE_H * 3);
    doc.setFont('helvetica', 'bold');
    y = pdfText(doc, `${label}:`, MARGIN, y, CONTENT_W);
    doc.setFont('helvetica', 'normal');
    y = pdfText(doc, value, MARGIN + 2, y, CONTENT_W - 2);
    y += 1.5;
  }

  y = ensureSpace(doc, y, 20);
  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Detalle de ítems', MARGIN, y);
  y += 6;

  const colWidths = [42, 14, 32, 22, 22, 22, 16];
  const headers = ['Material', 'Cant.', 'Responsable', 'Cédula', 'Serie', 'Marca', 'Evid.'];
  const tableX = MARGIN;
  let x = tableX;
  doc.setFontSize(8);
  doc.setFillColor(230, 230, 230);
  doc.rect(tableX, y - 4, CONTENT_W, 6, 'F');
  headers.forEach((h, i) => {
    doc.text(h, x + 1, y);
    x += colWidths[i];
  });
  y += 5;
  doc.setFont('helvetica', 'normal');

  items.forEach((row) => {
    y = ensureSpace(doc, y, LINE_H * 2);
    const cells = [
      row.materialName || '—',
      String(row.quantity ?? '—'),
      row.responsibleName || '—',
      row.responsibleDoc && !String(row.responsibleDoc).startsWith('vehicle:') ? row.responsibleDoc : '—',
      row.serial || '—',
      row.brandName || '—',
      String(row.evidenceCount ?? 0),
    ];
    let rowY = y;
    let maxLines = 1;
    const cellLines = cells.map((cell, i) => {
      const lines = doc.splitTextToSize(String(cell), colWidths[i] - 2);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });
    cellLines.forEach((lines, i) => {
      const cx = tableX + colWidths.slice(0, i).reduce((a, b) => a + b, 0) + 1;
      doc.text(lines, cx, rowY);
    });
    y += maxLines * LINE_H + 2;
  });

  return doc;
}

/**
 * PDF con la misma estructura de campos que AssignmentForm (sin ventana de impresión).
 * @param {object} data — type, date, movementId, entryRef, assigneeType, coGroup, warehouseId, materialCategory, observations, ítems, etc.
 */
export async function downloadAssignmentFormPdf(data) {
  const doc = buildAssignmentFormPdfDoc(data);
  doc.save(assignmentFormPdfFilename(data));
}
